import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playNotificationSound } from "./chat/chatUtils.js";

/**
 * IncomingCallPanel Component
 * App-level ringing card for incoming voice calls — mounted beside the
 * always-rendered ChatWindow so it rings even while the chat is closed.
 * Accepting also opens the chat window (handled by the onAccept prop).
 */
export function IncomingCallPanel({ call, darkMode = false, onAccept }) {
    const isRinging = call.status === "incoming";
    const { peer } = call;

    // Repeat the existing notification sound modestly while ringing; the
    // effect cleanup stops it on accept/decline/cancel/timeout.
    useEffect(() => {
        if (!isRinging) return undefined;
        playNotificationSound();
        const interval = setInterval(playNotificationSound, 3500);
        return () => clearInterval(interval);
    }, [isRinging]);

    return (
        <AnimatePresence>
            {isRinging && (
                <motion.div
                    key="incoming-call"
                    initial={{ opacity: 0, y: 24, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 24, scale: 0.95 }}
                    transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 28,
                        mass: 0.8,
                    }}
                    className={`tw-fixed tw-bottom-24 tw-right-6 tw-w-[320px] tw-max-w-[calc(100vw-3rem)] tw-rounded-2xl tw-shadow-2xl tw-border tw-p-4 ${
                        darkMode
                            ? "tw-bg-dark tw-border-gray-700"
                            : "tw-bg-white tw-border-gray-200"
                    }`}
                    style={{ zIndex: 2147483647 }}
                >
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-relative tw-flex-shrink-0">
                            <div
                                className={`tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-overflow-hidden ${
                                    darkMode
                                        ? "tw-bg-gray-800"
                                        : "tw-bg-primary/10"
                                }`}
                            >
                                {peer?.avatar ? (
                                    <img
                                        src={peer.avatar}
                                        alt={peer?.name || "Caller"}
                                        className="tw-w-full tw-h-full tw-object-cover"
                                    />
                                ) : (
                                    <svg
                                        className={`tw-w-6 tw-h-6 ${
                                            darkMode
                                                ? "tw-text-gray-400"
                                                : "tw-text-primary"
                                        }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                    </svg>
                                )}
                            </div>
                            <span className="tw-absolute tw--bottom-0.5 tw--right-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-emerald-500 tw-border-2 tw-border-white tw-flex tw-items-center tw-justify-center tw-animate-pulse" />
                        </div>
                        <div className="tw-flex-1 tw-min-w-0">
                            <h4
                                className={`tw-m-0 tw-text-sm tw-font-semibold tw-truncate ${
                                    darkMode
                                        ? "tw-text-gray-100"
                                        : "tw-text-gray-800"
                                }`}
                            >
                                {peer?.name || "Support"}
                            </h4>
                            <p
                                className={`tw-m-0 tw-text-xs tw-truncate ${
                                    darkMode
                                        ? "tw-text-gray-400"
                                        : "tw-text-gray-500"
                                }`}
                            >
                                {peer?.shopName && peer.shopName !== peer?.name
                                    ? `Incoming voice call · ${peer.shopName}`
                                    : "Incoming voice call…"}
                            </p>
                        </div>
                    </div>

                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-3">
                        <button
                            onClick={call.rejectCall}
                            className="tw-flex-1 tw-h-10 tw-rounded-full tw-bg-red-500 hover:tw-bg-red-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1.5"
                            aria-label="Decline call"
                        >
                            <svg
                                className="tw-w-4 tw-h-4 tw-rotate-[135deg]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                            </svg>
                            Decline
                        </button>
                        <button
                            onClick={onAccept}
                            className="tw-flex-1 tw-h-10 tw-rounded-full tw-bg-emerald-500 hover:tw-bg-emerald-600 tw-text-white tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1.5"
                            aria-label="Accept call"
                        >
                            <svg
                                className="tw-w-4 tw-h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                            </svg>
                            Accept
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default IncomingCallPanel;
